/**
 * Session-scoped memory of the last tables seeded through "Query a table",
 * per `(connectionId, database)`, so the picker can float them to the top.
 * Deliberately not persisted — it is a convenience for the current session,
 * and table names must never leave the renderer through analytics or disk
 * in a new store.
 */
import { create } from 'zustand';

import { MAX_RECENT_SEED_TABLES } from './tableSeedGroups';

type RecentSeedTablesState = {
  readonly recentByScope: Readonly<Record<string, readonly string[]>>;
  recordSeed: (connectionId: string, database: string | undefined, targetKey: string) => void;
};

export function recentSeedScopeKey(connectionId: string, database: string | undefined): string {
  return `${connectionId}|${database ?? ''}`;
}

export const useRecentSeedTablesStore = create<RecentSeedTablesState>((set) => ({
  recentByScope: {},
  recordSeed: (connectionId, database, targetKey) =>
    set((state) => {
      const scope = recentSeedScopeKey(connectionId, database);
      const previous = state.recentByScope[scope] ?? [];
      const next = [targetKey, ...previous.filter((k) => k !== targetKey)].slice(0, MAX_RECENT_SEED_TABLES);
      return { recentByScope: { ...state.recentByScope, [scope]: next } };
    }),
}));
